import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import {
  Compass,
  Bookmark,
  Building2,
  UsersRound,
  ChevronDown,
  LogOut,
  Settings,
  Loader,
  ShieldCheck,
  Hammer,
  Map,
  RotateCcw,
  LayoutDashboard,
  ChartColumn,
} from "lucide-react";

import { authClient } from "@/lib/auth-client";
import { resetIdentity } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import {
  WORK_ADVISORY_PLAN_NAME,
  applyStatusLabels,
} from "@/lib/domain-constants";
import { useSuretyMode } from "@/hooks/useSuretyMode";
import {
  DEMO_COMPANY_LOGO,
  DEMO_COMPANY_NAME,
} from "@/lib/workadvisory-demo";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Toaster } from "@/components/ui/sonner";
import { ActiveProfileProvider } from "@/providers/ActiveProfileProvider";
import { useTeam } from "@/hooks/queries/useTeam";
import { useStatusCounts } from "@/hooks/queries/useStatusCounts";
import { useMySubscription } from "@/hooks/queries/useMySubscription";
import { useBetaInformation } from "@/hooks/queries/useBetaInformation";
import { useResetOnboarding } from "@/hooks/mutations/useResetOnboarding";
import { ViewQuotaBadge } from "@/components/gating/ViewQuotaBadge";
import { AccessGate } from "@/components/gating/AccessGate";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { OnboardingRedirect } from "@/components/onboarding/OnboardingRedirect";
import { FeaturedPickModal } from "@/components/common/FeaturedPickModal";
import { UpsellProvider } from "@/providers/UpsellProvider";
import { CountrySwitcher } from "@/components/common/CountrySwitcher";

// @ts-ignore
import licitigo_logo from "../../assets/licitigo_logo.svg";

type Tab = {
  to: string;
  label: string;
  icon: typeof Compass;
  active: string;
  iconColor: string;
  showBadge?: boolean;
  adminOnly?: boolean;
  /** Activa solo con la ruta exacta (una pestaña padre de otras). */
  end?: boolean;
};

const TABS: Tab[] = [
  {
    to: "/dashboard",
    label: "Descubrimiento",
    icon: Compass,
    active: "bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-300",
    iconColor: "text-blue-600",
  },
  // {
  //   to: "/tdm",
  //   label: "TDM",
  //   icon: Map,
  //   active:
  //     "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-300",
  //   iconColor: "text-amber-600",
  //   adminOnly: true,
  // },
  {
    to: "/my-contractings",
    label: "Mis licitaciones",
    icon: Bookmark,
    active:
      "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-300",
    iconColor: "text-emerald-500",
    showBadge: true,
  },
  {
    to: "/companies",
    label: "Empresas",
    icon: Building2,
    active:
      "bg-purple-50 text-purple-600 dark:bg-purple-950/30 dark:text-purple-300",
    iconColor: "text-purple-600",
  },
];

// La navegación del portal Fianzas: misma isla, otras pestañas.
const SURETY_TABS: Tab[] = [
  {
    to: "/surety",
    label: "Panel",
    icon: LayoutDashboard,
    active: "bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-300",
    iconColor: "text-blue-600",
    end: true,
  },
  {
    to: "/surety/team",
    label: "Mi equipo",
    icon: UsersRound,
    active:
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300",
    iconColor: "text-emerald-500",
  },
  {
    to: "/surety/clients",
    label: "Clientes",
    icon: Building2,
    active:
      "bg-purple-50 text-purple-700 dark:bg-purple-950/30 dark:text-purple-300",
    iconColor: "text-purple-600",
  },
  {
    to: "/surety/market",
    label: "Mercado",
    icon: ChartColumn,
    active:
      "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300",
    iconColor: "text-amber-600",
  },
];

function SavedBadge({ active }: { active: boolean }) {
  const { data } = useStatusCounts();
  const count = data
    ? data.sinEstado +
      data.revisadas +
      data.negociando +
      data.vendidas +
      data.perdidas
    : 0;
  if (!count) return null;
  return (
    <span
      // Re-mount on every count change so the pop animation replays.
      key={count}
      className={cn(
        "ml-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-extrabold leading-none transition-colors",
        "duration-300 animate-in zoom-in-50",
        active
          ? "bg-emerald-500 text-white"
          : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
      )}
    >
      {count}
    </span>
  );
}

function UserMenu({ showName = false }: { showName?: boolean }) {
  const { data: session } = authClient.useSession();
  const { data: activeOrg } = authClient.useActiveOrganization();
  const { data: beta } = useBetaInformation();
  const resetOnboarding = useResetOnboarding();

  // Recarga dura, no `navigate`: los atoms de Better Auth y la caché de React
  // Query viven a nivel de módulo, así que sin reload la siguiente cuenta que
  // entre en esta pestaña hereda la organización y los datos de la anterior.
  async function signOut() {
    await authClient.signOut();
    resetIdentity();
    window.location.href = "/auth/login";
  }

  function confirmResetOnboarding(withDemoDocuments: boolean) {
    const confirmed = window.confirm(
      withDemoDocuments
        ? "Se borrarán las líneas, el seguimiento y los análisis de esta cuenta, y se cargarán los documentos de demo, para empezar el onboarding de cero. ¿Continuar?"
        : "Se borrarán las líneas, el seguimiento, los análisis y los documentos de esta cuenta, sin cargar los de demo, para empezar el onboarding de cero. ¿Continuar?",
    );
    if (confirmed) resetOnboarding.mutate(withDemoDocuments);
  }

  const name = session?.user?.name || session?.user?.email || "Usuario";
  const isAdmin =
    (session?.user as { role?: string | null } | undefined)?.role === "admin";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-full border border-slate-100 bg-card py-1 pl-1 pr-2.5 shadow-sm transition-colors hover:bg-accent dark:border-slate-800 dark:bg-slate-900">
          <Avatar className="h-8 w-8">
            <AvatarImage src={session?.user?.image || ""} alt={name} />
            <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
              {name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {showName && (
            <span className="hidden text-sm font-bold sm:inline">{name}</span>
          )}
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col">
          <span className="truncate font-medium">{name}</span>
          {activeOrg?.name && (
            <span className="truncate text-xs font-normal text-muted-foreground">
              {activeOrg.name}
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings/account" className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            Configuración
          </Link>
        </DropdownMenuItem>
        {isAdmin && (
          <>
            <DropdownMenuItem asChild>
              <Link to="/admin" className="cursor-pointer">
                <ShieldCheck className="mr-2 h-4 w-4" />
                Panel de administración
              </Link>
            </DropdownMenuItem>
            {/* Cuelga del menú y no de las tabs a propósito: es la vista del
                cliente, que el equipo abre para revisar lo que se le publicó. */}
            <DropdownMenuItem asChild>
              <Link to="/work-advisory" className="cursor-pointer">
                <Hammer className="mr-2 h-4 w-4" />
                Informe de obras (cliente)
              </Link>
            </DropdownMenuItem>
          </>
        )}
        {beta?.canResetOnboarding && (
          <>
            <DropdownMenuItem
              onClick={() => confirmResetOnboarding(true)}
              disabled={resetOnboarding.isPending}
              className="cursor-pointer"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reiniciar onboarding
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => confirmResetOnboarding(false)}
              disabled={resetOnboarding.isPending}
              className="cursor-pointer"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reiniciar sin documentos de demo
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={signOut} className="cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function useVisibleTabs() {
  const { data: session } = authClient.useSession();
  const isAdmin =
    (session?.user as { role?: string | null } | undefined)?.role === "admin";
  // Quien dirige un equipo entra a la misma página, pero para él es el tablero
  // de su gente: cambia el nombre y el icono, no la ruta.
  const { data: team } = useTeam();
  const leadsTeam = !!team?.isTeam && !!team.canManageSearchProfiles;
  const suretyMode = useSuretyMode();
  if (suretyMode) return SURETY_TABS;

  return TABS.filter((tab) => !tab.adminOnly || isAdmin).map((tab) =>
    leadsTeam && tab.to === "/my-contractings"
      ? { ...tab, label: "Mi equipo", icon: UsersRound }
      : tab,
  );
}

/**
 * Cabecera del plan ASESORIA DE OBRAS. La vista es el informe de la empresa, y
 * la barra se co-brandea con su logo y pierde la navegación: el plan abre una
 * sola vista y no hay a dónde ir.
 */
function WorkAdvisoryHeader() {
  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-100 bg-card/95 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/95">
      <div className="h-1 w-full bg-primary" />
      <div className="flex items-center justify-between gap-3 px-4 py-3 md:px-6">
        <div className="flex items-center gap-3">
          <img src={licitigo_logo} alt="Licitigo" className="h-7 w-auto" />
          <div className="h-7 w-px bg-slate-200 dark:bg-slate-700" />
          <img
            src={DEMO_COMPANY_LOGO}
            alt={DEMO_COMPANY_NAME}
            className="h-12 w-auto object-contain"
          />
        </div>
        {/* Sin campanita: el plan no genera notificaciones y la barra es la de un
            informe, no la de la app. */}
        {/* El período ya no vive acá: lo pone la página con el del informe que
            está mostrando, que puede ser uno del histórico. */}
        <UserMenu showName />
      </div>
    </header>
  );
}

function Header() {
  const visibleTabs = useVisibleTabs();

  return (
    // Desde `lg` es un grid de 3 columnas (no `justify-between`) para que la pill
    // quede centrada en la ventana aunque los costados tengan anchos distintos.
    // Por debajo no cabe: la navegación se va a la tab bar inferior y arriba solo
    // quedan el logo y las acciones de la cuenta.
    <header className="sticky top-0 z-40 flex w-full items-center justify-between gap-3 px-4 pb-3 pt-4 backdrop-blur-md md:px-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:pt-6 dark:border-slate-800 dark:bg-slate-900/70">
      <div className="flex items-center justify-start">
        <Link to="/dashboard" className="flex items-center lg:hidden">
          <img src={licitigo_logo} alt="Licitigo" className="h-7 w-auto" />
        </Link>
        <div className="hidden lg:block">
          <CountrySwitcher />
        </div>
      </div>

      <nav className="hidden items-center rounded-full border border-slate-100 bg-card px-4 py-3 shadow-md lg:flex dark:border-slate-800 dark:bg-slate-900">
        <Link to={visibleTabs[0]?.to ?? "/dashboard"} className="flex items-center pr-1">
          <img src={licitigo_logo} alt="Licitigo" className="h-8 w-auto" />
        </Link>
        <div className="mx-3 h-6 w-px bg-slate-200 dark:bg-slate-700" />
        <div className="flex items-center gap-0.5">
          {visibleTabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end ?? tab.to === "/dashboard"}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-bold transition-all duration-200",
                  isActive
                    ? cn(tab.active, "shadow-sm")
                    : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <tab.icon className={cn("h-4 w-4", tab.iconColor)} />
                  <span>{tab.label}</span>
                  {tab.showBadge && <SavedBadge active={isActive} />}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="flex items-center justify-end gap-2">
        <ViewQuotaBadge />
        <NotificationBell />
        <UserMenu />
      </div>
    </header>
  );
}

/**
 * Navegación de móvil/tablet: barra fija al pie, al alcance del pulgar. Reusa la
 * paleta de cada tab como pastilla detrás del ícono para que la sección activa se
 * lea igual que en la pill del desktop.
 */
function MobileTabBar() {
  const visibleTabs = useVisibleTabs();

  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t border-slate-100 bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden dark:border-slate-800 dark:bg-slate-900/95">
      {visibleTabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.to === "/dashboard"}
          className="flex flex-1 flex-col items-center gap-0.5 py-2"
        >
          {({ isActive }) => (
            <>
              <span
                className={cn(
                  "relative flex h-8 w-14 items-center justify-center rounded-full transition-colors",
                  isActive && tab.active,
                )}
              >
                <tab.icon
                  className={cn(
                    "h-5 w-5",
                    isActive ? tab.iconColor : "text-slate-400",
                  )}
                />
                {tab.showBadge && (
                  <span className="absolute -right-0.5 -top-0.5">
                    <SavedBadge active={isActive} />
                  </span>
                )}
              </span>
              <span
                className={cn(
                  "text-[10px] font-bold",
                  isActive
                    ? "text-slate-800 dark:text-slate-100"
                    : "text-slate-500 dark:text-slate-400",
                )}
              >
                {tab.label}
              </span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppLayout() {
  const { data: session } = authClient.useSession();
  const isOnboarding = useLocation().pathname === "/onboarding";
  const { data: subscription } = useMySubscription();
  // Antes de que rendericen las páginas: las etapas del pipeline se llaman distinto en Fianzas.
  applyStatusLabels(useSuretyMode());
  // El plan ASESORIA DE OBRAS tiene su propio marco: una sola vista, co-brandeada.
  const isWorkAdvisoryPlan = subscription?.planKey === WORK_ADVISORY_PLAN_NAME;
  const { data: activeOrg, isPending: activeOrgPending } =
    authClient.useActiveOrganization();
  const { data: organizations, isPending: organizationsPending } =
    authClient.useListOrganizations();
  const [activating, setActivating] = useState(false);

  // Ensure an organization is active so `requireBetaAccess` can resolve the
  // subscription on the backend.
  const needsActivation =
    !!session?.user &&
    activeOrg === null &&
    !!organizations &&
    organizations.length > 0;

  useEffect(() => {
    if (!needsActivation || activating) return;
    setActivating(true);
    (async () => {
      try {
        await authClient.organization.setActive({
          organizationId: organizations![0]!.id,
          organizationSlug: organizations![0]!.slug,
        });
      } finally {
        setActivating(false);
      }
    })();
  }, [needsActivation, activating, organizations]);

  // Block the data UI until we know which organization is active. Otherwise
  // every org-scoped query (quota, search profiles, beta info, dashboard…)
  // fires before the org is set, fails, and gets refetched after activation —
  // a request storm on first load. Rendering once the org is ready lets each
  // query run a single time, so no blanket `invalidateQueries()` is needed.
  if (
    activeOrgPending ||
    organizationsPending ||
    needsActivation ||
    activating
  ) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-6">
        <img src={licitigo_logo} alt="Licitigo" className="h-14 w-auto" />
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <ActiveProfileProvider>
      {/* Por dentro de las líneas de negocio y por fuera del onboarding: sus modales
          las leen, y el onboarding también topa con el límite al crear una. */}
      <UpsellProvider>
        <OnboardingRedirect />
        {isOnboarding ? (
          <AccessGate>
            <Outlet />
          </AccessGate>
        ) : (
          <>
            <div className="flex min-h-screen flex-col bg-background">
              {isWorkAdvisoryPlan ? <WorkAdvisoryHeader /> : <Header />}
              {/* El padding inferior deja respirar el contenido sobre la tab bar fija. */}
              <main
                className={cn(
                  "flex flex-1 flex-col",
                  !isWorkAdvisoryPlan && "pb-20 lg:pb-0",
                )}
              >
                <AccessGate>
                  <Outlet />
                </AccessGate>
              </main>
              {!isWorkAdvisoryPlan && <MobileTabBar />}
            </div>
            <FeaturedPickModal />
          </>
        )}
      </UpsellProvider>
      <Toaster richColors position="top-right" />
    </ActiveProfileProvider>
  );
}
